import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Activity, Droplets, Users, ShoppingBag, MoonStar, ArrowRight, Check, X,
  Apple, Play, MessageCircle, Menu,
} from "lucide-react";
import SmoothScroll from "@/components/SmoothScroll";
import { Reveal, MaskLines } from "@/components/motion";
import { useSeo, SITE_URL } from "@/lib/seo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const FEATURES = [
  { icon: MessageCircle, title: "Clára AI", copy: "A conversational coach that knows your data — ask her anything to understand your results, create a workout or meal plan, or discuss any health topic.",
    img: "/clara-card.jpg", pos: "center top" },
  { icon: Droplets, title: "Bloodwork deep-dive", copy: "Upload a lab PDF and watch every biomarker trend over time, colour-coded against your optimal ranges.",
    img: "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Users, title: "Coach marketplace", copy: "Work 1:1 with vetted coaches. Proposals, routines and messaging — billed seamlessly through Stripe.",
    img: "/coach-marketplace.jpg" },
  { icon: ShoppingBag, title: "Curated shop", copy: "UK-made supplements, manufactured to GMP standards and matched to your results.",
    img: "/shop-lineup.jpg" },
  { icon: MoonStar, title: "Cycle & recovery", copy: "Cycle-aware, sleep-aware coaching that adapts your plan to how your body actually recovers.",
    img: "/cycle-recovery.jpg", pos: "center 15%" },
];

const CHAPTERS = [
  { n: "01", t: "Everything in one place", d: "Bloodwork, nutrition, training, sleep and habits stop living in ten different apps. Better Days unifies them into a single health score you actually understand." },
  { n: "02", t: "Coaching that reads the data", d: "Your coach — human or AI — sees the same numbers you do. No more guessing. Every recommendation is grounded in your real biomarkers and recovery." },
  { n: "03", t: "Built for the long game", d: "Retest cadence, cycle-aware programming and longevity markers. We optimise for the next decade, not the next 30-day challenge." },
];

const COMPARE = [
  { label: "Bloodwork trends & personalised insights", a: { ok: true, t: "Interprets biomarkers, tracks trends, gives actionable guidance" }, b: { ok: false, t: "No bloodwork support" } },
  { label: "Recovery-aware coaching (sleep, RHR, stress)", a: { ok: true, t: "Adjusts training and habits based on your physiology" }, b: { ok: false, t: "Basic sleep logs only" } },
  { label: "Cycle-aware training & nutrition", a: { ok: true, t: "Programs adapt to hormonal phases" }, b: { ok: false, t: "No cycle intelligence" } },
  { label: "1:1 expert coach marketplace", a: { ok: true, t: "Access real coaches matched to your goals" }, b: { ok: false, t: "No personalised coaching" } },
  { label: "Training, nutrition & habit intelligence", a: { ok: true, t: "Integrated system that adapts daily" }, b: { ok: false, t: "Fragmented tracking only" } },
  { label: "Step counting", a: { ok: true, t: "Included, but part of a bigger picture" }, b: { ok: true, t: "Their main feature" } },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-surface/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/better-days-mark.png" alt="Better Days" className="h-6 w-auto" />
          <span className="text-[15px] font-bold tracking-tight text-ink">Better Days</span>
        </Link>
        <nav className="hidden items-center gap-8 text-sm font-medium text-muted md:flex">
          <a href="#features" className="hover:text-ink">Features</a>
          <a href="#compare" className="hover:text-ink">Why Better Days</a>
          <a href="#story" className="hover:text-ink">Our approach</a>
          <Link to="/shop" className="hover:text-ink" data-testid="nav-shop">Shop</Link>
          <Link to="/videos" className="hover:text-ink" data-testid="nav-videos">Videos</Link>
          <Link to="/biomarkers" className="hover:text-ink" data-testid="nav-biomarkers">Biomarkers</Link>
          <Link to="/sol" className="hover:text-ink" data-testid="nav-sol">Sol</Link>
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login" className="text-sm font-semibold text-ink hover:text-brand" data-testid="nav-login">Sign in</Link>
          <Link to="/register" data-testid="nav-signup"
            className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">Start free</Link>
        </div>

        {/* Mobile menu */}
        <div className="flex items-center gap-2 md:hidden">
          <Link to="/register" data-testid="nav-signup-mobile"
            className="rounded-full bg-brand px-3.5 py-1.5 text-sm font-semibold text-white">Start free</Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="landing-menu-btn" aria-label="Open menu"
                className="rounded-md border border-line p-2 text-ink hover:bg-rail">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-72 flex-col gap-1 p-6" data-testid="landing-nav-drawer">
              <SheetTitle className="mb-4 text-left text-base">Menu</SheetTitle>
              <a href="#features" onClick={close} className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Features</a>
              <a href="#compare" onClick={close} className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Why Better Days</a>
              <a href="#story" onClick={close} className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Our approach</a>
              <Link to="/shop" onClick={close} data-testid="mobile-nav-shop" className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Shop</Link>
              <Link to="/videos" onClick={close} data-testid="mobile-nav-videos" className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Videos</Link>
              <Link to="/biomarkers" onClick={close} data-testid="mobile-nav-biomarkers" className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Biomarkers</Link>
              <Link to="/sol" onClick={close} data-testid="mobile-nav-sol" className="rounded-md px-2 py-3 text-base font-medium text-ink hover:bg-rail">Sol</Link>
              <div className="mt-4 flex flex-col gap-3 border-t border-line pt-5">
                <Link to="/login" onClick={close} data-testid="mobile-nav-login" className="rounded-md border border-line px-4 py-2.5 text-center text-sm font-semibold text-ink hover:bg-rail">Sign in</Link>
                <Link to="/register" onClick={close} className="rounded-full bg-brand px-4 py-2.5 text-center text-sm font-semibold text-white">Start free</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="grain relative overflow-hidden brand-gradient pt-32 pb-16">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-brand-dark">
            <span className="h-1.5 w-1.5 rounded-full bg-brand" /> Now on web · synced with the app
          </motion.span>
          <h1 className="mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskLines lines={["Your AI-powered", "health coach —", "finally unified."]} start={0.15} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-6 max-w-md text-lg text-muted">
            Bloodwork, nutrition, training and habits in one place. Better Days turns your data into a plan you&apos;ll actually follow.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-signup"
              className="group flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lift transition-transform hover:-translate-y-0.5">
              Start free — desktop <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <div className="flex items-center gap-2">
              <a href={process.env.REACT_APP_APP_STORE_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:border-brand">
                <Apple className="h-4 w-4" /> App Store</a>
              <a href={process.env.REACT_APP_PLAY_STORE_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:border-brand">
                <Play className="h-4 w-4" /> Google Play</a>
            </div>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div style={{ y, scale }} className="relative overflow-hidden rounded-lg border border-line shadow-card">
            <img src="/hero.jpg"
              alt="Better Days" className="aspect-[4/5] w-full object-cover" loading="eager" fetchPriority="high" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
            className="absolute -bottom-5 -left-5 w-44 rounded-lg border border-line bg-white p-4 shadow-card">
            <p className="text-xs font-medium text-muted">Health Score</p>
            <p className="text-3xl font-bold text-brand">86</p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-rail"><div className="h-full w-[86%] rounded-full bg-brand" /></div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }}
            className="absolute -right-4 top-8 rounded-lg border border-line bg-white p-3 shadow-card">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-success"><Activity className="h-3.5 w-3.5" /> HRV +12%</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  useSeo({
    title: "Better Days — AI health coach: bloodwork, nutrition & training unified",
    description: "Better Days is an AI-powered health coach that unifies bloodwork tracking, nutrition, training and habits. Your supplement stack tracker and cycle-aware fitness companion.",
    path: "/",
    image: `${SITE_URL}/og/landing.jpg`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization", name: "Better Days", url: SITE_URL,
          logo: `${SITE_URL}/better-days-mark.png`,
        },
        {
          "@type": "WebSite", name: "Better Days", url: SITE_URL, inLanguage: "en-GB",
          publisher: { "@type": "Organization", name: "Better Days", url: SITE_URL },
        },
        {
          "@type": "SoftwareApplication", name: "Better Days",
          applicationCategory: "HealthApplication", operatingSystem: "iOS, Android, Web",
          description: "AI health coach that unifies bloodwork, nutrition, training and habits.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
        },
      ],
    },
  });

  return (
    <SmoothScroll>
      <div className="grain relative min-h-screen bg-surface">
        <Nav />
        <Hero />

        {/* Marquee */}
        <div className="border-y border-line bg-brand-wash py-4">
          <Marquee autoFill gradient={false} speed={40}>
            {["Bloodwork intelligence", "Cycle-aware coaching", "Supplement stack tracker", "Sleep & HRV insights", "1:1 coaches", "Longevity markers"].map((t, i) => (
              <span key={i} className="mx-8 text-lg font-semibold tracking-tight text-brand-dark">{t} <span className="mx-8 text-brand/40">✦</span></span>
            ))}
          </Marquee>
        </div>

        {/* Features */}
        <section id="features" className="mx-auto max-w-6xl px-5 py-24">
          <Reveal><p className="text-sm font-semibold uppercase tracking-widest text-brand">What&apos;s inside</p></Reveal>
          <Reveal delay={1}><h2 className="mt-2 max-w-2xl text-3xl font-bold tracking-tight text-ink sm:text-4xl">A complete operating system for your health</h2></Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i}>
                <div className="group h-full overflow-hidden rounded-lg border border-line bg-card transition-shadow hover:shadow-card">
                  <div className="overflow-hidden bg-rail">
                    <img src={f.img} alt={f.title} style={{ objectPosition: f.pos || "center" }} loading="lazy" className={`h-72 w-full ${f.fit === "contain" ? "object-contain p-2" : "object-cover"} transition-transform duration-700 group-hover:scale-105`} />
                  </div>
                  <div className="p-6">
                    <f.icon className="h-6 w-6 text-brand" strokeWidth={2} />
                    <h3 className="mt-3 text-lg font-bold tracking-tight text-ink">{f.title}</h3>
                    <p className="mt-1.5 text-sm text-muted">{f.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={5}>
              <Link to="/register" className="flex h-full min-h-[280px] flex-col justify-between rounded-lg bg-ink p-6 text-white transition-transform hover:-translate-y-1">
                <span className="text-sm font-semibold uppercase tracking-widest text-brand">Ready?</span>
                <span>
                  <span className="block text-2xl font-bold tracking-tight">Start your first health score today</span>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">Create account <ArrowRight className="h-4 w-4" /></span>
                </span>
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Story chapters */}
        <section id="story" className="border-y border-line bg-card">
          <div className="mx-auto max-w-6xl px-5 py-24">
            <Reveal><h2 className="max-w-2xl text-3xl font-bold tracking-tight text-ink sm:text-4xl">Why I built Better Days</h2></Reveal>
            <Reveal>
              <div className="mt-10 flex max-w-5xl flex-col gap-8 md:flex-row md:items-start" data-testid="founder-story">
                <div className="max-w-2xl border-l-2 border-brand pl-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">Founder story</p>
                <div className="mt-4 space-y-4 leading-relaxed text-muted">
                  <p className="text-ink">I built Better Days because I couldn't find an app that did what I needed. I track my bloods, my blood pressure, my diet and my training, but every app handled one piece and none of them told me the thing I actually wanted to know: how healthy am I overall, and what should I do next?</p>
                  <p>So I built it. Better Days pulls your health data into one place, including bloodwork and blood pressure, activity, nutrition and body composition and turns it into a single Health Score you can work on. It nudges you towards a balanced diet and regular training, with challenges and a social feed that keep you going.</p>
                  <p>I made the core app free because people shouldn't have to pay to understand their own health. It's funded by things I already buy myself, supplements and blood test kits, which fit naturally into the app. Personal trainers and nutritionists can also work with their clients inside Better Days, so nobody has to switch to a second app.</p>
                  <p className="font-medium text-ink">I'm building the product I wanted as a user, and that's the standard I hold every feature to.</p>
                  <p className="pt-1 text-sm font-semibold tracking-wide text-brand">— Chris</p>
                </div>
              </div>
                <img
                  src="/founder-chris.jpg"
                  alt="Chris, founder of Better Days"
                  data-testid="founder-photo"
                  loading="lazy"
                  className="aspect-[3/4] w-40 shrink-0 rounded-2xl border border-line object-cover shadow-sm md:ml-4 md:mt-6 md:aspect-auto md:w-72 md:self-stretch lg:w-80"
                />
              </div>
            </Reveal>
            <div className="mt-14 space-y-px">
              {CHAPTERS.map((c) => (
                <Reveal key={c.n}>
                  <div className="grid gap-4 border-t border-line py-10 md:grid-cols-[120px_1fr] md:gap-12">
                    <span className="text-5xl font-bold tracking-tighter text-brand/25">{c.n}</span>
                    <div>
                      <h3 className="text-2xl font-bold tracking-tight text-ink">{c.t}</h3>
                      <p className="mt-3 max-w-2xl text-muted">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section id="compare" className="mx-auto max-w-5xl px-5 py-24">
          <Reveal><h2 className="text-center text-3xl font-bold tracking-tight text-ink sm:text-4xl">Not just another tracker</h2></Reveal>
          <Reveal delay={1}>
            <div className="mt-10 overflow-hidden rounded-lg border border-line bg-card">
              <div className="grid grid-cols-[1.2fr_1.6fr_1fr] items-center border-b border-line bg-rail/60 px-6 py-4 text-sm font-semibold">
                <span className="text-ink">Capability</span>
                <span className="text-brand">Better Days</span>
                <span className="text-muted">Generic apps</span>
              </div>
              {COMPARE.map(({ label, a, b }, i) => (
                <div key={i} className="grid grid-cols-[1.2fr_1.6fr_1fr] items-start gap-4 border-b border-line px-6 py-4 text-sm last:border-0">
                  <span className="font-semibold text-ink">{label}</span>
                  <span className="flex items-start gap-2 text-muted">
                    {a.ok ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-line-strong" />}
                    <span>{a.t}</span>
                  </span>
                  <span className="flex items-start gap-2 text-muted">
                    {b.ok ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-line-strong" />}
                    <span>{b.t}</span>
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* Download CTA */}
        <section className="mx-auto max-w-6xl px-5 py-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-ink px-8 py-16 text-white sm:px-16">
              <div className="relative z-10 max-w-xl">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Start on the web. Live it on mobile.</h2>
                <p className="mt-4 text-white/70">Create your account here, then take Clára, meal scanning and workout tracking with you everywhere.</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/register" className="rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-dark">Start free on web</Link>
                  <a href={process.env.REACT_APP_APP_STORE_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-3 text-sm font-semibold hover:bg-white/20"><Apple className="h-4 w-4" /> App Store</a>
                  <a href={process.env.REACT_APP_PLAY_STORE_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-3 text-sm font-semibold hover:bg-white/20"><Play className="h-4 w-4" /> Google Play</a>
                </div>
              </div>
              <img src="https://images.unsplash.com/photo-1664883247910-c83c54ccdd76?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="app" className="pointer-events-none absolute -right-10 -top-10 hidden h-[130%] w-1/2 object-cover opacity-30 md:block" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
            </div>
          </Reveal>
        </section>

        {/* Footer */}
        <footer className="border-t border-line bg-card">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-10 sm:flex-row">
            <div className="flex items-center gap-2.5">
              <img src="/better-days-mark.png" alt="Better Days" className="h-5 w-auto" />
              <span className="font-bold tracking-tight text-ink">Better Days</span>
            </div>
            <p className="text-sm text-muted">© {new Date().getFullYear()} BETTER DAYS HEALTH LIMITED</p>
            <div className="flex gap-6 text-sm text-muted">
              <Link to="/login" className="hover:text-ink">Sign in</Link>
              <Link to="/register" className="hover:text-ink">Get started</Link>
              <Link to="/privacy-policy" className="hover:text-ink" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/returns-policy" className="hover:text-ink" data-testid="footer-returns-link">Returns Policy</Link>
            </div>
          </div>
        </footer>
      </div>
    </SmoothScroll>
  );
}
